/**
 * Home Page Component
 * @implements TS-032 - Customer Portal Frontend Package Setup
 * @implements FS-001.4.1 - Role-Based Routing
 *
 * Landing page that redirects authenticated users to their dashboard.
 */

import { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/providers';

export const HomePage = () => {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const navigate = useNavigate();

  // Redirect authenticated users to their primary dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.primaryDashboard) {
      navigate(user.primaryDashboard, { replace: true });
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If authenticated, show loading while redirect happens
  if (isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          py: 4,
        }}
      >
        <Typography variant="h2" component="h1" gutterBottom>
          Jared Customer Portal
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          Welcome to the Jared platform. Manage your projects and tasks with
          ease.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            size="large"
          >
            Login
          </Button>
          <Button
            component={RouterLink}
            to="/register"
            variant="outlined"
            size="large"
          >
            Register
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};
